import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { useAdminRole } from '@/hooks/useAdminRole';
import { useFavorites } from '@/hooks/useFavorites';
import LanguageSwitcher from './LanguageSwitcher';
import SearchOverlay from './SearchOverlay';

interface NavbarProps {
  dark?: boolean;
}

export default function Navbar({ dark = false }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { isSuperAdmin } = useAdminRole();
  const { favoriteIds } = useFavorites();
  const favoriteCount = favoriteIds.size;
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) setSignedIn(!!data.session);
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setSignedIn(!!session);
    });
    return () => {
      cancelled = true;
      listener.subscription.unsubscribe();
    };
  }, []);

  // White text when floating over a dark hero image, or when the navbar turns
  // into a solid teal bar on scroll.
  const lightText = scrolled || dark;

  const brandColor = lightText ? 'text-white' : 'text-secondary-900';
  const subBrandColor = lightText ? 'text-white/70' : 'text-secondary-600';
  const linkColor = lightText
    ? 'text-white/90 hover:text-white'
    : 'text-secondary-700 hover:text-primary-600';
  const iconColor = lightText
    ? 'text-white/80 hover:text-white'
    : 'text-secondary-600 hover:text-primary-600';
  const mobileBtnColor = lightText ? 'text-white' : 'text-secondary-700';
  const signInBtnColor = lightText
    ? 'bg-white text-primary-600 hover:bg-white/90'
    : 'bg-primary-500 text-white hover:bg-primary-600';

  const navLinks = [
    { label: t('nav.tours'), href: '/tours' },
    { label: t('nav.specialServices'), href: '/special-services' },
    { label: t('nav.blog'), href: '/blog' },
    { label: t('nav.about'), href: '/about' },
    { label: t('nav.whyUs'), href: '/why-us' },
    { label: t('nav.contact'), href: '/contact' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-primary-500' : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-1 shrink-0">
            <span className={`font-heading text-xl md:text-2xl font-bold tracking-tight ${brandColor}`}>
              Vietnam
            </span>
            <span className={`text-[10px] md:text-xs font-medium tracking-widest uppercase ${subBrandColor}`}>
              STORY
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                className={`text-sm font-medium transition-colors whitespace-nowrap ${linkColor}`}
              >
                {link.label}
              </Link>
            ))}
            {isSuperAdmin && (
              <Link
                to="/admin"
                className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-md px-3 py-1.5 transition-colors whitespace-nowrap bg-secondary-900 text-white hover:bg-secondary-800"
              >
                <i className="ri-shield-star-line text-sm" />
                {t('nav.admin')}
              </Link>
            )}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-3 md:gap-4">
            <Link
              to={signedIn ? '/profile' : '/login'}
              className={`hidden md:inline-flex items-center gap-2 text-sm font-semibold rounded-md px-3 md:px-4 py-2 transition-colors whitespace-nowrap ${signInBtnColor}`}
            >
              <i className={signedIn ? 'ri-user-3-line' : 'ri-user-line'} />
              <span className="hidden sm:inline">{signedIn ? t('nav.myAccount') : t('nav.signIn')}</span>
            </Link>
            <button
              className={`w-8 h-8 flex items-center justify-center transition-colors ${iconColor}`}
              onClick={() => setSearchOpen(true)}
              aria-label={t('search.title')}
            >
              <i className="ri-search-line text-lg" />
            </button>
            <LanguageSwitcher light={lightText} />
            <Link
              to={signedIn ? '/profile?tab=saved' : '/login'}
              className={`relative flex w-8 h-8 items-center justify-center transition-colors ${iconColor}`}
              aria-label={t('nav.savedToursAria', { count: favoriteCount })}
            >
              <i className={`${favoriteCount > 0 ? 'ri-heart-3-fill' : 'ri-heart-3-line'} text-lg`} />
              {favoriteCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-accent-500 text-white text-[10px] font-bold leading-none">
                  {favoriteCount > 99 ? '99+' : favoriteCount}
                </span>
              )}
            </Link>
            <button
              className={`lg:hidden w-8 h-8 flex items-center justify-center ${mobileBtnColor}`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <i className={mobileMenuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-secondary-200 shadow-lg">
          <div className="px-4 py-4 space-y-3">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                className="block text-sm font-medium text-secondary-700 hover:text-primary-600 py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            {isSuperAdmin && (
              <Link
                to="/admin"
                className="flex items-center gap-2 text-sm font-semibold text-secondary-900 py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className="ri-shield-star-line text-base" />
                {t('nav.adminPanel')}
              </Link>
            )}
            <Link
              to={signedIn ? '/profile?tab=saved' : '/login'}
              className="flex items-center gap-2 text-sm font-medium text-secondary-700 hover:text-primary-600 py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className={`${favoriteCount > 0 ? 'ri-heart-3-fill' : 'ri-heart-3-line'} text-base`} />
              {t('nav.savedTours')}
              {favoriteCount > 0 && (
                <span className="ml-auto min-w-[18px] h-5 px-1.5 flex items-center justify-center rounded-full bg-accent-500 text-white text-[11px] font-bold">
                  {favoriteCount > 99 ? '99+' : favoriteCount}
                </span>
              )}
            </Link>
            <div className="pt-3 border-t border-secondary-200">
              <Link
                to={signedIn ? '/profile' : '/login'}
                className="flex items-center justify-center gap-2 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-md px-4 py-2.5 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className={signedIn ? 'ri-user-3-line' : 'ri-user-line'} />
                {signedIn ? t('nav.myAccount') : t('nav.signIn')}
              </Link>
            </div>
          </div>
        </div>
      )}

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </nav>
  );
}